import { useState, useRef, useEffect, useMemo } from 'react';
import { useGetDemos } from '../../hooks/useDemoApi';

const CustomDemoSelect = ({ selectedDemoId, onDemoChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [allDemos, setAllDemos] = useState([]);
  const dropdownRef = useRef(null);
  const observerRef = useRef(null);
  
  const limit = 10;
  
  const { data, isLoading, isFetching } = useGetDemos(page, limit);
  
  const demos = useMemo(() => data?.demos || [], [data?.demos]);
  const totalPages = data?.totalPages || 1;

  useEffect(() => {
    if (demos.length > 0) {
      setAllDemos(prev => {
        const existingIds = new Set(prev.map(d => d._id));
        const newDemos = demos.filter(d => !existingIds.has(d._id));
        return [...prev, ...newDemos];
      });
    }
  }, [demos]);

  const selectedDemo = allDemos.find(demo => demo._id === selectedDemoId);

  // Filter demos based on search
  const filteredDemos = allDemos.filter(demo =>
    demo.demoName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    if (!isOpen) return;

    const lastOptionElement = document.querySelector('[data-last-option]');
    
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && page < totalPages && !isFetching) {
          setPage(prev => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (lastOptionElement) {
      observerRef.current.observe(lastOptionElement);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [isOpen, page, totalPages, isFetching]);

  const handleSelect = (demoId) => {
    onDemoChange({ target: { value: demoId } });
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleToggle = () => {
    if (!isOpen) {
      setPage(1);
      setSearchQuery('');
    }
    setIsOpen(!isOpen);
  };

  return (
    <div ref={dropdownRef} className="relative w-full">
      <label className="block text-sm font-medium text-gray-700">
        Function
      </label>
      
      {/* Selected Value Display */}
      <button
        type="button"
        onClick={handleToggle}
        className="w-full mt-1 border rounded-md px-3 py-2 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between gap-2"
      >
        <span className={`truncate ${selectedDemo ? 'text-gray-900' : 'text-gray-500'}`}>
          {selectedDemo ? selectedDemo.demoName || 'Unnamed Demo' : 'All Demos'}
        </span>
        <svg
          className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white border rounded-md shadow-lg max-h-80 overflow-hidden min-w-full w-max max-w-xs">
          <div className="overflow-y-auto max-h-80">
            {/* "All Demos" Option */}
            <button
              type="button"
              onClick={() => handleSelect('')}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 whitespace-nowrap ${
                selectedDemoId === '' ? 'bg-blue-50 text-blue-600 font-medium' : ''
              }`}
            >
              All Demos
            </button>

            {/* Demo Options */}
            {isLoading && page === 1 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center whitespace-nowrap">
                Loading demos...
              </div>
            ) : filteredDemos.length > 0 ? (
              <>
                {filteredDemos.map((demo, index) => (
                  <button
                    key={demo._id}
                    type="button"
                    onClick={() => handleSelect(demo._id)}
                    data-last-option={index === filteredDemos.length - 1 ? 'true' : undefined}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 whitespace-nowrap ${
                      selectedDemoId === demo._id ? 'bg-blue-50 text-blue-600 font-medium' : ''
                    }`}
                  >
                    {demo.demoName || 'Unnamed Demo'}
                  </button>
                ))}
                
                {/* Loading More Indicator */}
                {isFetching && page > 1 && (
                  <div className="px-3 py-2 text-sm text-gray-500 text-center whitespace-nowrap">
                    Loading more...
                  </div>
                )}
              </>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 text-center whitespace-nowrap">
                No demos found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDemoSelect;
